export const simplifyPoints = (
  points,
  tolerance = 0.0005,
  targetPointCount = null
) => {
  if (points.length <= 2) return points.slice();
  if (targetPointCount && points.length <= targetPointCount)
    return points.slice();

  // Convert points to Float32Array for lat/lng only (other properties preserved later)
  const coords = new Float32Array(points.length * 2);
  for (let i = 0; i < points.length; i++) {
    coords[i * 2] = points[i].lat; // lat
    coords[i * 2 + 1] = points[i].lng; // lng
  }

  // Square distance from a point to a segment
  const getSqSegDist = (pIdx, p1Idx, p2Idx) => {
    let x = coords[p1Idx * 2], // p1.lat
      y = coords[p1Idx * 2 + 1], // p1.lng
      dx = coords[p2Idx * 2] - x, // p2.lat - p1.lat
      dy = coords[p2Idx * 2 + 1] - y; // p2.lng - p1.lng

    if (dx !== 0 || dy !== 0) {
      const t =
        ((coords[pIdx * 2] - x) * dx + (coords[pIdx * 2 + 1] - y) * dy) /
        (dx * dx + dy * dy);
      if (t > 1) {
        x = coords[p2Idx * 2];
        y = coords[p2Idx * 2 + 1];
      } else if (t > 0) {
        x += dx * t;
        y += dy * t;
      }
    }

    const latDiff = coords[pIdx * 2] - x;
    const lngDiff = coords[pIdx * 2 + 1] - y;
    return latDiff * latDiff + lngDiff * lngDiff;
  };

  // Douglas-Peucker simplification with dynamic tolerance
  const simplifyWithTolerance = (sqTolerance) => {
    const stack = [[0, points.length - 1]];
    const keep = new Uint8Array(points.length); // Track which points to keep
    keep[0] = keep[points.length - 1] = 1; // Always keep first and last

    while (stack.length) {
      const [first, last] = stack.pop();
      let maxSqDist = sqTolerance;
      let index = first;

      for (let i = first + 1; i < last; i++) {
        const sqDist = getSqSegDist(i, first, last);
        if (sqDist > maxSqDist) {
          index = i;
          maxSqDist = sqDist;
        }
      }

      if (maxSqDist > sqTolerance) {
        keep[index] = 1;
        if (last - index > 1) stack.push([index, last]);
        if (index - first > 1) stack.push([first, index]);
      }
    }

    const simplified = [];
    for (let i = 0; i < points.length; i++) {
      if (keep[i]) simplified.push(points[i]);
    }
    return simplified;
  };

  // If no targetPointCount is specified, use the provided tolerance
  if (!targetPointCount) {
    return simplifyWithTolerance(tolerance * tolerance);
  }

  // Binary search to find the right tolerance for targetPointCount
  let minTolerance = 0;
  let maxTolerance = 1; // Upper bound can be adjusted based on your data
  let simplified = [];

  for (let i = 0; i < 20; i++) {
    // Limit iterations to avoid infinite loops
    const midTolerance = (minTolerance + maxTolerance) / 2;
    simplified = simplifyWithTolerance(midTolerance * midTolerance);

    if (simplified.length === targetPointCount) {
      break;
    } else if (simplified.length > targetPointCount) {
      minTolerance = midTolerance;
    } else {
      maxTolerance = midTolerance;
    }
  }

  return simplified;
};
